import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import IconButton from '@material-ui/core/IconButton';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { AlertHelpDialog } from '@iris-platform/iris-react-ui';
import { HasPermission } from '@iris-platform/protektor';
import { withApollo } from 'react-apollo';
import {
  includes,
  replace,
  trim,
} from 'ramda';
import ScopeEditor from './ScopeEditor';
import { handleChange, stylesToPropType } from '../../../utils';
import applicationPropType from '../applicationPropType';
import Helpfile from '../helpfile.json';
import { PROTEKTOR_VIEW, PROTEKTOR_UPDATE } from '../../../utils/protektorConstants';

const styles = () => ({
  root: {
    display: 'flex',
    flexFlow: 'column nowrap',
    fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
    color: '#282828',
    '& > *': {
      marginTop: '0.4em',
    },
  },
  divider: {
    margin: '2em 0',
  },
  input: {
    marginTop: '0.6em',
    '& input': {
      padding: '0.4em',
      color: '#666666',
    },
  },
  saveButton: {
    borderRadius: '0.125em',
    marginTop: '1em',
    width: '5em',
  },
  icon: {
    // margin: theme.spacing.unit,
    fontSize: 14,
    cursor: 'pointer',
  },
  iconbutton: {
    width: '18px',
    height: '18px',
  },
  scopeSuggestions: {
    display: 'flex',
    flexFlow: 'row wrap',
  },
  helper: {
    display: 'flex',
    height: '28.8px',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    animation: '0.8s slidein',
  },
  '@keyframes slidein': {
    '0%': {
      opacity: '0',
      height: '0',
      marginTop: '0',
      transform: 'scaleY(0)',
      transformOrigin: 'top',
    },
    '100%': {
      opacity: '1',
      transform: 'scaleY(1)',
      transformOrigin: 'top',
    },
  },
});

// const BASE_SCOPE = 'client';
const CLIENT_BASE_SCOPE = 'irisbaseclient';
const SERVER_BASE_SCOPE = 'irisbaseserver';

class UnstyledTokensTab extends Component {
  constructor(props) {
    super(props);

    const { application } = props;
    this.state = {
      application: {
        applicationID: application.applicationID,
        jwtTokenExpiration: application.jwtTokenExpiration,
        clientTokenScopes: application.clientTokenScopes,
        jwtServerTokenExpiration: application.jwtServerTokenExpiration,
        serverTokenScopes: application.serverTokenScopes,
      },
      error: null,
      pendingChanges: false,
      saving: false,
      open: false,
      dialogTitle: '',
      dialogContent: '',
    };
  }

  handleHelpOpen = (title, content) => {
    this.setState({
      open: true,
      dialogTitle: title,
      dialogContent: content,
    });
  };

  handleHelpClose = () => {
    this.setState({
      open: false,
      dialogTitle: '',
      dialogContent: '',
    });
  };

  // saveChanges = async () => {
  saveChanges = () => {
    const { ownerId, refreshApp, enqueueSnackbar } = this.props;
    const {
      application: {
        applicationID,
        jwtTokenExpiration,
        jwtServerTokenExpiration,
        clientTokenScopes,
        serverTokenScopes,
      },
    } = this.state;
    // const currentTeam = TeamContext.getSelectedTeamId();
    const currentTeam = ownerId;
    const application = {
      applicationID,
      jwtTokenExpiration,
      jwtServerTokenExpiration,
      clientTokenScopes,
      serverTokenScopes,
    };

    this.setState({ saving: true });

    refreshApp(application, currentTeam);

    enqueueSnackbar('Tokens successfully updated', { variant: 'success' });

    this.setState({ pendingChanges: false });
    this.setState({ saving: false });
  }

  expandIdsScopes = scopeString => (includes('ids', scopeString) && !includes(':ids', scopeString)
    ? trim(replace('ids', 'readwrite:ids', scopeString))
    : scopeString);

  render() {
    const { classes, role } = this.props;
    const {
      application: {
        jwtTokenExpiration,
        jwtServerTokenExpiration,
        clientTokenScopes,
        serverTokenScopes,
      },
      error,
      pendingChanges,
      saving,
      open,
      dialogTitle,
      dialogContent,
    } = this.state;
    const help = Helpfile.tokens20;

    return (
      <Fragment>
        <form>
          <FormControl className={classes.root} error={error}>
            {saving && (
              <LinearProgress />
            )}

            {error && (
              <FormHelperText className={classes.helper}>{error.message}</FormHelperText>
            )}

            <HasPermission
              to={PROTEKTOR_VIEW}
              access="Application_Tokens_Tab_Client_Tokens"
              forRole={role}
            >
              <Fragment>
                <Typography
                  component="div"
                  variant="subheading"
                  gutterBottom
                >
                  Client Tokens
                  <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.clientToken.title, help.clientToken.content)}>
                    <HelpOutlineIcon className={classes.icon} />
                  </IconButton>
                </Typography>
                <TextField
                  label="Token Expiration (minutes)"
                  value={jwtTokenExpiration}
                  onChange={(event) => {
                    handleChange(this, ['application', 'jwtTokenExpiration'])(event);
                    this.setState({ pendingChanges: true });
                  }}
                  type="number"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  disabled={!role.hasPermission({ action: PROTEKTOR_UPDATE, resource: 'Application_Tokens_Tab_Client_Tokens' })}
                />
                <br />
                <ScopeEditor
                  baseScope={CLIENT_BASE_SCOPE}
                  scopeString={this.expandIdsScopes(clientTokenScopes)}
                  onChange={clientScopes => this.setState(state => ({
                    pendingChanges: true,
                    application: {
                      ...state.application,
                      clientTokenScopes: clientScopes,
                    },
                  }))}
                  disabled={!role.hasPermission({ action: PROTEKTOR_UPDATE, resource: 'Application_Tokens_Tab_Client_Tokens' })}
                />
              </Fragment>
            </HasPermission>
            <br />
            <br />
            <HasPermission
              to={PROTEKTOR_VIEW}
              access="Application_Tokens_Tab_Server_Tokens"
              forRole={role}
            >
              <Fragment>
                <Typography
                  component="div"
                  variant="subheading"
                  gutterBottom
                >
                  Server Tokens
                  <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.serverToken.title, help.serverToken.content)}>
                    <HelpOutlineIcon className={classes.icon} />
                  </IconButton>
                </Typography>
                <TextField
                  label="Token Expiration (minutes)"
                  value={jwtServerTokenExpiration}
                  onChange={(event) => {
                    handleChange(this, ['application', 'jwtServerTokenExpiration'])(event);
                    this.setState({ pendingChanges: true });
                  }}
                  type="number"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  disabled={!role.hasPermission({ action: PROTEKTOR_UPDATE, resource: 'Application_Tokens_Tab_Server_Tokens' })}
                />
                <br />
                <ScopeEditor
                  baseScope={SERVER_BASE_SCOPE}
                  scopeString={this.expandIdsScopes(serverTokenScopes)}
                  onChange={serverScopes => this.setState(state => ({
                    pendingChanges: true,
                    application: {
                      ...state.application,
                      serverTokenScopes: serverScopes,
                    },
                  }))}
                  disabled={!role.hasPermission({ action: PROTEKTOR_UPDATE, resource: 'Application_Tokens_Tab_Server_Tokens' })}
                />
              </Fragment>
            </HasPermission>
            <br />
            <Button
              variant="contained"
              classes={{ root: classes.saveButton }}
              disabled={!pendingChanges
                || (!role.hasPermission({ action: PROTEKTOR_UPDATE, resource: 'Application_Tokens_Tab_Server_Tokens' })
                && !role.hasPermission({ action: PROTEKTOR_UPDATE, resource: 'Application_Tokens_Tab_Client_Tokens' }))
              }
              onClick={this.saveChanges}
              // onClick={refreshApp}
            >
              Save
            </Button>
          </FormControl>
        </form>
        <AlertHelpDialog
          handleClose={this.handleHelpClose}
          open={open}
          dialogTitle={dialogTitle}
          dialogContent={dialogContent}
        />
      </Fragment>
    );
  }
}

UnstyledTokensTab.propTypes = {
  application: applicationPropType.isRequired,
  classes: stylesToPropType(styles()).isRequired,
  refreshApp: PropTypes.func.isRequired,
  ownerId: PropTypes.string.isRequired,
  client: PropTypes.object.isRequired,
  role: PropTypes.object.isRequired,
};

const TokensTab = withStyles(styles)(UnstyledTokensTab);

export default withSnackbar(withApollo(TokensTab));
